import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { REQUIRE_AUTH } from '../interceptors/auth-context';

export interface GracePeriodStatus {
  isInGracePeriod: boolean;
  startDate: Date | null;
  endDate: Date | null;
  daysRemaining: number;
  activeProfessionalsCount: number;
  paidProfessionalsCount: number;
  unpaidProfessionalsCount: number;
  unpaidAmount: number;
  reason: string | null;
  pricePerProfessional: number;
  professionalsToDeactivate: ProfessionalToDeactivate[];
}

export interface ProfessionalToDeactivate {
  userId: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  appointmentsCount: number;
  createdAt: Date;
}

export interface PaymentResponse {
  businessMembershipId: string;
}

@Injectable({
  providedIn: 'root'
})
export class GracePeriodService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/api/v1/membership`;
  private readonly DISMISS_KEY = 'gracePeriodBannerDismissed';
  private readonly DISMISS_DURATION = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

  private gracePeriodStatusSubject = new BehaviorSubject<GracePeriodStatus | null>(null);
  public gracePeriodStatus$ = this.gracePeriodStatusSubject.asObservable();

  constructor() {
    // Start polling for grace period status every 5 minutes
    this.startPolling();
  }

  private startPolling(): void {
    // Check right away, then every 5 minutes. The first check is async so the request
    // isn't sent while this service is still being injected: the membership interceptor
    // injects MembershipService, which depends on this service (NG0200 circular dependency).
    timer(0, 5 * 60 * 1000)
      .pipe(
        switchMap(() => this.checkGracePeriodStatus())
      )
      .subscribe();
  }

  getGracePeriodStatus(): Observable<GracePeriodStatus> {
    return this.http.get<GracePeriodStatus>(`${this.apiUrl}/grace-period/status`, {
      context: new HttpContext().set(REQUIRE_AUTH, true)
    });
  }

  checkGracePeriodStatus(): Observable<GracePeriodStatus> {
    return this.getGracePeriodStatus().pipe(
      tap(status => this.gracePeriodStatusSubject.next(status))
    );
  }

  payUnpaidProfessionals(amountPaid: number, paymentReference: string): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(`${this.apiUrl}/grace-period/pay`, {
      amountPaid,
      paymentReference
    }, {
      context: new HttpContext().set(REQUIRE_AUTH, true)
    });
  }

  dismissBanner(): void {
    const dismissedUntil = Date.now() + this.DISMISS_DURATION;
    localStorage.setItem(this.DISMISS_KEY, dismissedUntil.toString());
  }

  isBannerDismissed(): boolean {
    const dismissedUntil = localStorage.getItem(this.DISMISS_KEY);
    if (!dismissedUntil) {
      return false;
    }

    const dismissedTime = parseInt(dismissedUntil, 10);
    if (Date.now() > dismissedTime) {
      // Dismiss period expired, remove from storage
      localStorage.removeItem(this.DISMISS_KEY);
      return false;
    }

    return true;
  }

  clearDismissal(): void {
    localStorage.removeItem(this.DISMISS_KEY);
  }
}

